// Dynamic Line/Area Chart Component - Handles any data structure

import React, { useMemo, useState, useEffect } from 'react';
import { TrendingUp } from 'lucide-react';
import { AreaChart as RechartsAreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Brush, LabelList } from 'recharts';
import CustomTooltip from '../common/CustomTooltip';
import { COLORS, CHART_COLORS } from '../../constants/theme';
import { truncateLabel, formatNumber, getOptimalLabelLength } from '../../utils/chartUtils';

// Hook to track window size
const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1024,
    height: typeof window !== 'undefined' ? window.innerHeight : 768,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
};

// Format date for better readability
const formatDate = (dateStr) => {
  if (!dateStr) return dateStr;
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return dateStr;
  }
};


// Custom label component for data points
const CustomLabel = ({ x, y, value, color, isMobile }) => {
  if (!value || value === 0) return null;
  return (
    <text
      x={x}
      y={y - 10}
      fill={color}
      fontSize={isMobile ? 11 : 12}
      fontWeight={600}
      textAnchor="middle"
      style={{ pointerEvents: 'none' }}
    >
      {formatNumber(value)}
    </text>
  );
};

// Build a count-by-category aggregation when no numeric data exists
const buildCountSeries = (rows) => {
  const keys = Object.keys(rows[0]);
  const PREFERRED = ['status', 'type', 'market', 'country', 'priority', 'category', 'region'];
  const candidates = keys.filter(k => {
    if (k === 'id') return false;
    const uniques = new Set(rows.map(r => r[k]).filter(v => v != null && String(v).trim() !== '')).size;
    return uniques >= 2 && uniques <= Math.max(rows.length, 10);
  });
  candidates.sort((a, b) => {
    const ai = PREFERRED.findIndex(p => a.toLowerCase().includes(p));
    const bi = PREFERRED.findIndex(p => b.toLowerCase().includes(p));
    if (ai !== -1 && bi === -1) return -1;
    if (bi !== -1 && ai === -1) return 1;
    return new Set(rows.map(r => r[a])).size - new Set(rows.map(r => r[b])).size;
  });
  const groupKey = candidates[0] || keys[0];
  const counts = {};
  rows.forEach(row => {
    const val = String(row[groupKey] ?? 'Unknown').trim() || 'Unknown';
    counts[val] = (counts[val] || 0) + 1;
  });
  const aggregated = Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 15)
    .map(([label, count]) => ({ [groupKey]: label, Count: count }));
  return { xAxisKey: groupKey, yAxisKeys: ['Count'], colors: [COLORS.primary], isDate: false, isCountChart: true, groupKey, aggregated };
};

const AreaChart = ({ data, isArea = false }) => {
  const [showDataLabels] = useState(false);
  const { width: windowWidth } = useWindowSize();
  
  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;
  const isDesktop = windowWidth >= 1024;
  const chartHeight = isMobile ? 450 : isTablet ? 520 : 580;
  const fontSize = isMobile ? 13 : isTablet ? 14 : 15;
  const yAxisWidth = isMobile ? 60 : isTablet ? 80 : 90;

  // Dynamically detect columns from data
  const chartConfig = useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0) return null;
    const firstRow = data[0];
    if (!firstRow || typeof firstRow !== 'object') return null;
    const keys = Object.keys(firstRow);
    if (keys.length === 0) return null;

    // Find the best X-axis column (prefer dates then short strings)
    const xAxisKey = keys.find(key => {
      const value = firstRow[key];
      if (value === null || value === undefined) return false;
      const str = String(value);
      return str.length < 50 && (
        str.match(/^\d{4}-\d{2}-\d{2}/) ||
        str.match(/^\d{2}-\w{3}-\d{4}/) ||
        typeof value === 'string'
      );
    }) || keys[0];

    // Find numeric columns
    const numericKeys = keys.filter(key => {
      if (key === xAxisKey) return false;
      const values = data.map(row => {
        const v = row[key];
        if (v === null || v === undefined) return null;
        if (typeof v === 'number') return v;
        if (typeof v === 'string' && v.trim() !== '') {
          const p = Number(v.replace(/,/g, '').trim());
          return Number.isNaN(p) ? null : p;
        }
        return null;
      }).filter(v => typeof v === 'number');
      return values.length > 0;
    });

    // No numeric columns → count chart
    if (numericKeys.length === 0) return buildCountSeries(data);

    const ranked = numericKeys.map(key => {
      const values = data.map(row => {
        const v = row[key];
        if (v === null || v === undefined) return null;
        if (typeof v === 'number') return v;
        if (typeof v === 'string') { const p = Number(v.replace(/,/g, '').trim()); return isNaN(p) ? null : p; }
        return null;
      }).filter(v => typeof v === 'number');
      if (!values.length) return { key, variance: 0, max: 0 };
      const mean = values.reduce((a, b) => a + b, 0) / values.length;
      const variance = values.reduce((acc, v) => acc + Math.pow(v - mean, 2), 0) / values.length;
      return { key, variance, max: Math.max(...values) };
    }).sort((a, b) => b.variance - a.variance);

    // All zeros → count chart
    if (!ranked.some(r => r.max > 0)) return buildCountSeries(data);

    const yAxisKeys = ranked.slice(0, 3).map(r => r.key);
    const isDate = !!String(firstRow[xAxisKey]).match(/^\d{4}-\d{2}-\d{2}/);
    return { xAxisKey, yAxisKeys, colors: CHART_COLORS.slice(0, yAxisKeys.length), isDate, isCountChart: false };
  }, [data]);

  // Transform data to ensure numeric values
  const transformedData = useMemo(() => {
    if (!data || !Array.isArray(data) || !chartConfig) return [];
    if (chartConfig.isCountChart) return chartConfig.aggregated;

    return data.map((row, idx) => {
      const transformed = { ...row };
      if (chartConfig.xAxisKey && transformed[chartConfig.xAxisKey] !== undefined) {
        const xValue = transformed[chartConfig.xAxisKey];
        transformed[chartConfig.xAxisKey] = String(xValue || `Item ${idx + 1}`);
        transformed[`${chartConfig.xAxisKey}_original`] = xValue;
      }
      chartConfig.yAxisKeys.forEach(key => {
        let value = transformed[key];
        if (value !== undefined && value !== null) {
          if (typeof value === 'string') value = value.replace(/,/g, '').trim();
          const num = Number(value);
          transformed[key] = isNaN(num) || !isFinite(num) ? 0 : num;
        } else {
          transformed[key] = 0;
        }
      });
      return transformed;
    });
  }, [data, chartConfig]);

  // Calculate max value for Y-axis domain
  const maxValue = useMemo(() => {
    if (!transformedData.length || !chartConfig) return 100;
    let max = 0;
    transformedData.forEach(row => {
      chartConfig.yAxisKeys.forEach(key => {
        const value = Number(row[key]) || 0;
        if (value > max && isFinite(value)) max = value;
      });
    });
    // Ensure minimum domain for better visualization
    const calculatedMax = max * 1.1;
    return calculatedMax > 0 ? calculatedMax : 100;
  }, [transformedData, chartConfig]);

  const summaryStats = useMemo(() => {
    if (!chartConfig || !transformedData.length) return null;
    const primaryKey = chartConfig.yAxisKeys[0];
    if (!primaryKey) return null;
    const series = transformedData.map((row) => Number(row[primaryKey]) || 0);
    const max = Math.max(...series);
    const min = Math.min(...series);
    const avg = series.reduce((a, b) => a + b, 0) / series.length;
    const first = series[0] || 0;
    const last = series[series.length - 1] || 0;
    const trend = last - first;
    return { primaryKey, max, min, avg, trend };
  }, [chartConfig, transformedData]);

  // Calculate optimal X-axis configuration based on data and screen size
  const xAxisConfig = useMemo(() => {
    if (!transformedData.length || !chartConfig) {
      return { angle: 0, height: 50, interval: 0, marginBottom: isMobile ? 60 : isTablet ? 70 : 70 };
    }

    const dataLength = transformedData.length;
    const sampleXValue = transformedData[0]?.[chartConfig.xAxisKey];
    const avgLabelLength = sampleXValue ? String(sampleXValue).length : 10;

    // For large datasets or long labels, use rotation and spacing
    if (dataLength > 20 || avgLabelLength > 20) {
      const isRotated = true;
      const maxLength = getOptimalLabelLength(isMobile, isTablet, dataLength, isRotated);
      return {
        angle: -45,
        height: isMobile ? 120 : isTablet ? 125 : 120,
        interval: dataLength > 30 ? 'preserveStartEnd' : (isMobile ? Math.ceil(dataLength / 10) : Math.ceil(dataLength / 12)),
        marginBottom: isMobile ? 130 : isTablet ? 140 : 150,
        tickFormatter: (value) => truncateLabel(value, maxLength)
      };
    } else if (dataLength > 10) {
      const isRotated = true;
      const maxLength = getOptimalLabelLength(isMobile, isTablet, dataLength, isRotated);
      return {
        angle: -45,
        height: isMobile ? 90 : isTablet ? 100 : 100,
        interval: isMobile ? 'preserveStartEnd' : 0,
        marginBottom: isMobile ? 100 : isTablet ? 110 : 120,
        tickFormatter: (value) => truncateLabel(value, maxLength)
      };
    }

    const isRotated = false;
    const maxLength = getOptimalLabelLength(isMobile, isTablet, dataLength, isRotated);
    return {
      angle: 0,
      height: isMobile ? 50 : isTablet ? 55 : 60,
      interval: 0,
      marginBottom: isMobile ? 60 : isTablet ? 70 : 80,
      tickFormatter: (value) => truncateLabel(value, maxLength)
    };
  }, [transformedData, chartConfig, isMobile, isTablet]);

  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <div className="bg-white rounded-lg p-8 text-center border border-gray-200">
        <p className="text-gray-500">No data available for chart visualization.</p>
      </div>
    );
  }

  if (!chartConfig || chartConfig.yAxisKeys.length === 0) {
    return (
      <div className="bg-white rounded-lg p-8 text-center border border-gray-200">
        <p className="text-gray-500">No numeric data available for line chart.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in-up">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0 animate-fade-in-down">
        <div className="flex-1 min-w-0">
          <h3 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 animate-float flex-shrink-0" style={{ color: COLORS.primary }} />
            <span className="truncate">
              {chartConfig.isCountChart
                ? `Distribution by ${chartConfig.groupKey}`
                : `${isArea ? 'Area Chart' : 'Line Chart'} - Data Trend Analysis`}
            </span>
          </h3>
          <p className="text-xs sm:text-sm text-gray-600 mt-1 animate-fade-in">
            {chartConfig.isCountChart
              ? `${transformedData.length} categories across ${data.length} records`
              : `${data.length} data point${data.length !== 1 ? 's' : ''} across ${chartConfig.yAxisKeys.length} metric${chartConfig.yAxisKeys.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        {summaryStats && (
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div className="bg-slate-50 border border-slate-200 rounded-md px-2 py-1.5">
              <p className="text-slate-500">Peak</p>
              <p className="font-semibold text-slate-900">{formatNumber(summaryStats.max)}</p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-md px-2 py-1.5">
              <p className="text-slate-500">Average</p>
              <p className="font-semibold text-slate-900">{formatNumber(summaryStats.avg)}</p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-md px-2 py-1.5">
              <p className="text-slate-500">Trend</p>
              <p className={`font-semibold ${summaryStats.trend >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                {summaryStats.trend >= 0 ? '+' : ''}
                {formatNumber(summaryStats.trend)}
              </p>
            </div>
          </div>
        )}
      </div>
      <div className="bg-white rounded-lg p-4 sm:p-5 md:p-6 border border-gray-200 shadow-sm chart-animate card-hover">
        <ResponsiveContainer width="100%" height={chartHeight}>
          <RechartsAreaChart 
            data={transformedData}
            margin={{ 
              top: 30, 
              right: isMobile ? 25 : isTablet ? 40 : 50, 
              left: isMobile ? 50 : isTablet ? 50 : 50, 
              bottom: xAxisConfig.marginBottom 
            }}
          >
            <defs>
              {chartConfig.yAxisKeys.map((key, idx) => {
                const color = chartConfig.colors[idx % chartConfig.colors.length];
                return (
                  <linearGradient key={key} id={`color${key}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={color} stopOpacity={0.4}/>
                    <stop offset="50%" stopColor={color} stopOpacity={0.25}/>
                    <stop offset="95%" stopColor={color} stopOpacity={0.05}/>
                  </linearGradient>
                );
              })}
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" opacity={0.6} />
            <XAxis 
              dataKey={chartConfig.xAxisKey}
              stroke={COLORS.secondary}
              style={{ fontSize: `${fontSize}px`, fontWeight: '600' }}
              tick={{ fill: '#4B5563', fontSize: fontSize }}
              tickFormatter={(value) => {
                if (chartConfig.isDate) return formatDate(value);
                return xAxisConfig.tickFormatter ? xAxisConfig.tickFormatter(value) : truncateLabel(value, getOptimalLabelLength(isMobile, isTablet, transformedData.length, xAxisConfig.angle !== 0));
              }}
              angle={xAxisConfig.angle}
              textAnchor={xAxisConfig.angle < 0 ? 'end' : 'middle'}
              height={xAxisConfig.height}
              interval={xAxisConfig.interval}
              minTickGap={isMobile ? 5 : isTablet ? 6 : 8}
            />
            <YAxis 
              stroke={COLORS.secondary}
              style={{ fontSize: `${fontSize}px`, fontWeight: '600' }}
              tick={{ fill: '#4B5563', fontSize: fontSize }}
              tickFormatter={formatNumber}
              domain={[0, maxValue]}
              width={yAxisWidth}
            />
            <Tooltip 
              content={<CustomTooltip />}
              cursor={{ stroke: COLORS.primary, strokeWidth: 2, strokeDasharray: '5 5', opacity: 0.3 }}
              animationDuration={300}
              animationEasing="ease-out"
            />
            <Legend 
              wrapperStyle={{ fontSize: `${fontSize}px`, fontWeight: '600', paddingTop: '25px' }}
              iconType="line"
              iconSize={isMobile ? 16 : 18}
            />
            {chartConfig.yAxisKeys.map((key, idx) => {
              const color = chartConfig.colors[idx % chartConfig.colors.length];
              // Only show dots for smaller datasets to avoid clutter
              const showDots = showDataLabels && transformedData.length <= 20;
              return (
                <Area 
                  key={key}
                  type="monotone" 
                  dataKey={key} 
                  stroke={color} 
                  strokeWidth={2.5} 
                  fillOpacity={0.6} 
                  fill={`url(#color${key})`} 
                  connectNulls={false}
                  dot={showDots ? { 
                    r: 4, 
                    fill: color, 
                    cursor: 'pointer',
                    strokeWidth: 2,
                    stroke: '#fff'
                  } : false} 
                  activeDot={{ 
                    r: 8, 
                    fill: color, 
                    stroke: '#fff', 
                    strokeWidth: 3,
                    style: { filter: 'drop-shadow(0 3px 6px rgba(0,0,0,0.25))' }
                  }}
                  name={key}
                  style={{ cursor: 'pointer' }}
                  onClick={(data) => {
                    console.log('Area clicked:', data);
                  }}
                >
                  {showDataLabels && transformedData.length <= 15 && (
                    <LabelList 
                      content={(props) => (
                        <CustomLabel 
                          x={props.x} 
                          y={props.y} 
                          value={props.value} 
                          color={color}
                          isMobile={isMobile}
                        />
                      )}
                    />
                  )}
                </Area>
              );
            })}
            {transformedData.length > 15 && (
              <Brush 
                dataKey={chartConfig.xAxisKey}
                height={35}
                stroke={COLORS.primary}
                fill="#f3f4f6"
                tickFormatter={(value) => {
                  if (chartConfig.isDate) return formatDate(value);
                  return truncateLabel(value, 15);
                }}
                style={{ opacity: 0.8 }}
              />
            )}
          </RechartsAreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default AreaChart;
